// Upload with exponential backoff + jitter. Payload (FormData) is held by the caller and reused
// across attempts. Auth headers: X-FF-Key always; Authorization: Bearer when getToken() is set —
// the token is re-fetched on EVERY attempt so a short-lived token can't go stale during backoff.

const BACKOFF_MS = [2000, 4000, 8000, 16000]; // attempt delays; ~5 attempts total
// Generous per-attempt window: a 20-minute session can be tens of MB and slow uplinks are real.
// This is a stuck-connection backstop, not a latency budget.
const ATTEMPT_TIMEOUT_MS = 5 * 60 * 1000;

export async function upload(cfg, formData, { onAttempt } = {}) {
  const maxAttempts = BACKOFF_MS.length + 1;
  let lastErr;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (onAttempt) onAttempt(attempt + 1, maxAttempts);
    try {
      const headers = await buildHeaders(cfg);
      const res = await postOnce(cfg.uploadUrl, headers, formData);
      if (res.ok) return { ok: true, status: res.status };
      // 409 = duplicate sessionId = the server already has this submission (idempotent success).
      if (res.status === 409) return { ok: true, status: 409, duplicate: true };
      // Other 4xx (except 408/429) won't succeed on retry.
      if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
        return { ok: false, status: res.status, fatal: true };
      }
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      lastErr = e;
    }

    if (attempt < maxAttempts - 1) {
      await sleep(BACKOFF_MS[attempt] + jitter(BACKOFF_MS[attempt]));
    }
  }

  return { ok: false, error: String(lastErr || 'upload failed') };
}

async function buildHeaders(cfg) {
  const headers = {};
  if (cfg.ingestKey) headers['X-FF-Key'] = cfg.ingestKey;
  if (typeof cfg.getToken === 'function') {
    try {
      const token = await cfg.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (e) {
      console.warn('[ff] getToken() failed', e);
    }
  }
  return headers;
}

function postOnce(url, headers, body) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ATTEMPT_TIMEOUT_MS);
  return fetch(url, { method: 'POST', headers, body, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

function jitter(base) { return Math.floor(Math.random() * base * 0.3); }
function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }
